import { useCallback, useEffect, useState } from "react";
import { Copy, Link2, MonitorSmartphone, RefreshCw, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, money } from "../../lib/api";

const shortDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
import { loadRazorpay } from "../../lib/checkout";
import { DatePicker } from "./DatePicker";
import { StatusPill, rupeesToMinor } from "./PaymentsDesk";

const EMPTY = { amount: "", purpose: "", name: "", email: "", phone: "", expire_at: "" };

const openCheckout = async (p) => {
  await loadRazorpay();
  return new Promise((resolve, reject) => {
    new window.Razorpay({
      key: p.razorpay_key_id, order_id: p.provider_order_id, amount: p.amount_minor, currency: "INR", name: "AVAILITY", description: p.purpose,
      prefill: { name: p.name, email: p.email, contact: `+91${p.phone}` }, theme: { color: "#15365F" },
      handler: async (res) => { try { resolve((await api.post(`/admin/desk/payins/${p.id}/verify`, res)).data); } catch (e) { reject(e); } },
      modal: { ondismiss: () => reject(new Error("Checkout closed before payment")) },
    }).open();
  });
};

const PayinForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (mode) => {
    if (!(Number(f.amount) >= 1)) { toast.error("Enter an amount of at least ₹1"); return; }
    setBusy(mode);
    try {
      const body = { mode, amount_minor: rupeesToMinor(f.amount), purpose: f.purpose, name: f.name, email: f.email, phone: f.phone, expire_at: mode === "link" && f.expire_at ? new Date(`${f.expire_at}T23:59:00`).toISOString() : null };
      const p = (await api.post("/admin/desk/payins", body)).data;
      if (mode === "link") toast.success("Payment link sent by email and SMS");
      else { const paid = await openCheckout(p); toast.success(`Payment received: ${money(paid.amount_minor)}`); }
      setF(EMPTY);
    } catch (e) { toast.error(e.response ? formatError(e) : e.message); }
    finally { setBusy(""); onCreated(); }
  };
  return (
    <form className="admin-form desk-card" onSubmit={(e) => { e.preventDefault(); submit("link"); }} data-testid="payin-form">
      <h3>Collect a payment</h3>
      <div className="form-row two">
        <label className="form-field"><span>Amount (₹)</span><input data-testid="payin-amount-input" type="number" min="1" step="0.01" value={f.amount} onChange={set("amount")} required /></label>
        <label className="form-field"><span>Purpose</span><input data-testid="payin-purpose-input" value={f.purpose} onChange={set("purpose")} minLength={3} maxLength={200} required /></label>
      </div>
      <div className="form-row two">
        <label className="form-field"><span>Payer name</span><input data-testid="payin-name-input" value={f.name} onChange={set("name")} minLength={2} required /></label>
        <label className="form-field"><span>Payer email</span><input data-testid="payin-email-input" type="email" value={f.email} onChange={set("email")} required /></label>
      </div>
      <div className="form-row two">
        <label className="form-field"><span>Payer mobile (10 digits)</span><input data-testid="payin-phone-input" value={f.phone} onChange={set("phone")} pattern="[6-9][0-9]{9}" required /></label>
        <div className="form-field"><span>Link expires end of (optional)</span><DatePicker testId="payin-expiry-input" placeholder="No expiry" value={f.expire_at} onChange={(v) => setF({ ...f, expire_at: v })} disabledBefore={new Date()} /></div>
      </div>
      <div className="desk-actions">
        <button className="button primary" data-testid="payin-send-link-button" disabled={!!busy}><Link2 size={16} /> {busy === "link" ? "Sending…" : "Send payment link"}</button>
        <button type="button" className="button secondary" data-testid="payin-collect-now-button" disabled={!!busy} onClick={(e) => e.currentTarget.form.reportValidity() && submit("checkout")}><MonitorSmartphone size={16} /> {busy === "checkout" ? "Opening…" : "Collect now"}</button>
      </div>
    </form>
  );
};

const PayinRow = ({ p, reload }) => {
  const act = async (path, msg) => { try { await api.post(`/admin/desk/payins/${p.id}/${path}`); toast.success(msg); reload(); } catch (e) { toast.error(formatError(e)); } };
  const copy = () => navigator.clipboard.writeText(p.short_url).then(() => toast.success("Link copied"), () => toast.error("Could not copy"));
  const open = p.mode === "link" && p.status === "created";
  return (
    <tr data-testid={`payin-row-${p.id}`}>
      <td>{shortDate(p.date)}</td><td><b>{p.name}</b><small>{p.contact}</small></td><td>{p.purpose}<small>{p.mode === "link" ? "Payment link" : "Collect now"}</small></td>
      <td><b>{money(p.amount_minor)}</b></td><td><StatusPill status={p.status} testId={`payin-status-${p.id}`} /></td>
      <td><div className="row-actions">
        {p.short_url && open && <button className="icon-button" title="Copy link" aria-label="Copy link" data-testid={`payin-copy-${p.id}`} onClick={copy}><Copy size={15} /></button>}
        {open && <button className="icon-button" title="Resend" aria-label="Resend" data-testid={`payin-resend-${p.id}`} onClick={() => act("resend", "Link resent")}><Send size={15} /></button>}
        {open && <button className="icon-button danger" title="Cancel link" aria-label="Cancel link" data-testid={`payin-cancel-${p.id}`} onClick={() => window.confirm("Cancel this payment link?") && act("cancel", "Link cancelled")}><XCircle size={15} /></button>}
        <button className="icon-button" title="Refresh status" aria-label="Refresh status" data-testid={`payin-refresh-${p.id}`} onClick={() => act("refresh", "Status refreshed")}><RefreshCw size={15} /></button>
      </div></td>
    </tr>
  );
};

export const DeskCollect = ({ enabled, onChange }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/admin/desk/ledger", { params: { type: "payin" } }).then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e))); onChange(); }, [onChange]);
  useEffect(() => { load(); }, [load]);
  if (!enabled) return <div className="notice" data-testid="payins-locked">Razorpay gateway keys are not configured, so pay-ins are unavailable.</div>;
  return (
    <div className="desk-stack">
      <PayinForm onCreated={load} />
      <div className="table-wrap"><table className="admin-table" data-testid="payins-table"><thead><tr><th>Date</th><th>Payer</th><th>Purpose</th><th>Amount</th><th>Status</th><th /></tr></thead>
        <tbody>{rows.slice(0, 50).map((p) => <PayinRow key={p.id} p={p} reload={load} />)}</tbody></table>
        {!rows.length && <div className="empty-state" data-testid="payins-empty-state">No pay-ins yet.</div>}</div>
    </div>
  );
};
